import type { Plan } from "./plan.js";

/**
 * Cat Mail 도메인 타입.
 *
 * 핵심 아이디어: 메일 스레드가 아니라 "사람(참여자 집합)"이 방의 정체성이다.
 * 제목이 바뀌어도 같은 사람과의 대화면 같은 방에 쌓이고, 제목이 바뀐 지점에만
 * 구분선(subjectShift)이 들어간다. 일반 메일 클라이언트의 스레드 모델과 다르다.
 */

// ---------------------------------------------------------------------------
// 방
// ---------------------------------------------------------------------------

/** 좌측 다크 레일의 필터 탭. `quiet`는 별도 보관함이라 all에 포함되지 않는다. */
export type RoomFilter = "all" | "direct" | "group" | "quiet";

export type RoomKind = "direct" | "group";

/**
 * 상대가 지금 어떤 상태인지. 아바타 우하단 점 4종에 대응한다.
 *
 * 점도 문구도 **디스코드 그대로**다(온라인·자리 비움·오프라인·방해 금지).
 * 메일에 실시간 접속은 없으므로 무엇을 근거로 삼을지만 이 앱에 맞게 바꾼다 —
 * 접속 여부 대신 대화가 굴러가는 상태로 판정한다(`computeStatus`).
 */
export type PeerStatus =
  /** 초록 · 온라인 — 최근 양방향으로 주고받는 중 */
  | "active"
  /** 노랑 · 자리 비움 — 내가 마지막으로 보냈고 아직 답이 없음 */
  | "awaiting"
  /** 회색 · 오프라인 — 오래 조용함 */
  | "idle"
  /** 빨강 · 방해 금지 — 발신전용 주소(no-reply). 답장 불가 */
  | "noreply";

/**
 * 같은 서버 사람이 **지금 붙어 있는가.**
 *
 * `PeerStatus`와 헷갈리지 않게 따로 둔다. 저건 메일이라 접속을 알 수 없어서
 * 대화가 굴러가는 모양으로 **추측한** 것이고, 이건 실제로 아는 것이다. 같은
 * 서버 사람에게는 추측할 이유가 없으므로 이쪽이 이긴다.
 */
export type Presence =
  /** SSE가 붙어 있다 */
  | "online"
  /** 방금까지 있었다. 디스코드의 자리 비움과 같은 자리지만 근거는 다르다 */
  | "away"
  | "offline";

export type Participant = {
  id: string;
  /** 소문자로 정규화된 이메일 주소 */
  address: string;
  /** 사용자가 바꾼 이름이 있으면 그것, 없으면 메일 From의 표시이름 */
  displayName: string;
  /** 아바타 이니셜. 한글은 1~2자, 영문은 1자 */
  initials: string;
  /** 팔레트 인덱스(0~10). address 해시로 고정 배정 */
  colorIndex: number;
  avatarUrl: string | null;
  status: PeerStatus;
  /**
   * 차단한 상대인가. 차단하면 앞으로 오는 메일을 저장하지 않는다.
   * 이미 받은 것은 남는다 — 지우는 것과 안 받는 것은 다르다.
   */
  blocked: boolean;
  /** 나 자신인가 */
  isSelf: boolean;
  /**
   * 같은 Cat Mail 서버 사람인가. 이쪽으로 보낸 말은 인터넷을 돌지 않고 바로 꽂힌다
   * (apps/api/src/mail/localDelivery.ts).
   */
  internal: boolean;
  /** 접속 상태. 바깥 사람은 알 길이 없으므로 null이다. */
  presence: Presence | null;
};

/** 방 목록에 한 줄로 보여줄 미리보기 */
export type RoomPreview = {
  /** 그룹방일 때 "박지연: " 처럼 앞에 붙는 이름. 1:1이면 null */
  senderName: string | null;
  text: string;
};

export type Room = {
  id: string;
  /**
   * 이 방이 속한 내 주소.
   *
   * 방은 주소에 속한다 — `netflix@`로 온 대화와 `amazon@`으로 온 대화는 상대가
   * 같아도 다른 방이다(architecture.md §1). 화면이 계정을 갈아탈 때 **지금 보는
   * 방이 갈아탄 계정 것인지** 알아야 해서 같이 내려보낸다. 없으면 링크로 열어둔
   * 방이 남의 계정 것인 채로 남는다.
   *
   * 옛 API는 이 값을 안 보낸다. 없으면 판단하지 않는다.
   */
  identityId?: string;
  kind: RoomKind;
  /** 조용한 방(광고·뉴스레터)으로 분류되었는가. 이 방은 읽기 전용이다. */
  isQuiet: boolean;
  /** 사용자가 직접 붙인 방 이름. 없으면 참여자 이름으로 조합해 표시 */
  title: string | null;
  participants: Participant[];
  unreadCount: number;
  lastMessageAt: string | null;
  preview: RoomPreview | null;
  /** 사용자가 이 방을 조용한 방으로 직접 옮겼는가(자동 분류를 덮어씀) */
  pinnedQuiet: boolean;
  createdAt: string;
};

// ---------------------------------------------------------------------------
// 메시지
// ---------------------------------------------------------------------------

export type MessageDirection = "incoming" | "outgoing";

export type DeliveryState =
  | "queued"
  | "sending"
  | "sent"
  | "delivered"
  | "failed";

export type Attachment = {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  /** 인라인 이미지(cid:)인지 첨부인지 */
  inline: boolean;
  /**
   * 본문이 `cid:`로 이 첨부를 가리킬 때 쓰는 값. 꺾쇠는 벗긴 상태다.
   * 이게 없으면 원문 HTML의 박힌 이미지를 어느 파일로 채울지 알 수 없다.
   */
  contentId: string | null;
  /**
   * 5MiB 발신 제한을 넘겨 R2로 올라간 파일. 본문에는 링크만 실린다.
   * 수신 첨부는 항상 false.
   */
  offloaded: boolean;
  downloadUrl: string;
};

export type Message = {
  id: string;
  roomId: string;
  direction: MessageDirection;
  senderId: string;
  /** 메일 제목. 방 안에서는 바뀔 때만 구분선으로 노출된다. */
  subject: string;
  /** 인용문·서명을 걷어낸 "이번에 새로 쓴 부분"만. 말풍선에 들어가는 내용 */
  body: string;
  /** 원문 HTML이 있으면 07 화면(HTML 메일 뷰어)에서 열 수 있다. */
  hasHtml: boolean;
  attachments: Attachment[];
  sentAt: string;
  readAt: string | null;
  /** 그룹방에서 이 메시지를 읽은 사람 수 */
  readBy: number;
  delivery: DeliveryState;
  /** 발신 실패 사유 (delivery === "failed"일 때) */
  error: string | null;
};

/**
 * 대화 흐름에 끼워 넣는 비메시지 요소.
 * 렌더링할 때 Message와 한 배열로 합쳐 시간순 정렬한다.
 */
export type TimelineItem =
  | { kind: "message"; at: string; message: Message }
  /** "8월 5일 화요일" 날짜 칩 */
  | { kind: "dateChip"; at: string; label: string }
  /** "다른 이야기 · 9월 워크숍" — 제목이 바뀐 지점 */
  /**
   * 제목이 같은 메시지 묶음의 머리. `first`면 대화(또는 지금 불러온 구간)의
   * 첫 묶음이라 문구가 달라진다.
   */
  | { kind: "subjectShift"; at: string; subject: string; first: boolean }
  /** "이 채팅방은 알림이 꺼져 있어요" 같은 방 상태 안내 */
  | { kind: "notice"; at: string; text: string };

// ---------------------------------------------------------------------------
// 계정
// ---------------------------------------------------------------------------

/**
 * 새로 온 메일이 어디로 갈지. 내 프로필 팝오버의 "새 메일" 드롭다운.
 *
 * 화면 문구는 `SelfProfilePopover`의 RECEIVE_LABEL 한 곳에서만 정한다.
 */
export type ReceiveMode =
  /** 모두 대화 목록으로 */
  | "all"
  /** 주고받은 적 있는 주소만 대화 목록으로, 나머지는 조용한 방으로 */
  | "known"
  /** 모두 조용한 방으로 */
  | "quiet";

/**
 * 위 타입의 값 목록. 타입만으로는 런타임에 검사할 수 없어서 따로 둔다.
 * 화면의 선택지 순서이기도 하다.
 */
export const RECEIVE_MODES = ["all", "known", "quiet"] as const satisfies readonly ReceiveMode[];

/**
 * 최고 관리자가 받아줬는가.
 *
 * 계정과 주소가 같은 세 값을 쓴다. 둘 다 "사람이 보고 연다"는 같은 관문을 지나고,
 * 승인의 알맹이도 같다 — 엣지 allowlist에 주소를 넣는 것이다.
 *
 * **없을 수도 있다.** 옛 API는 이 값을 안 보낸다. `undefined`와 `"pending"`은
 * 다르다 — 없다고 대기로 치면 이미 승인된 사람이 승인 화면에 갇힌다.
 */
export type ApprovalStatus = "pending" | "approved" | "rejected";

export type MailAddress = {
  id: string;
  address: string;
  /** 기본 발신 주소인가 */
  isPrimary: boolean;
  label: string | null;
  /**
   * 주인이 켜둔 주소인가. **`status`와 다르다** — 저건 관리자가 받아줬는지이고
   * 이건 내가 잠시 닫아둔 것이다. 꺼도 이력은 남고, 다시 켜면 그대로 돌아온다.
   *
   * 옛 API는 이 값을 안 보낸다. 없으면 켜진 것으로 본다 — 멀쩡한 주소를 꺼진
   * 것으로 그리면 쓸 수 있는 주소를 못 쓰는 줄 안다.
   */
  enabled?: boolean;
  /** 승인을 기다리는 주소도 목록에 뜬다. 안 보이면 신청한 줄 모르고 또 신청한다 */
  status?: ApprovalStatus;
};

export type CurrentUser = {
  id: string;
  displayName: string;
  initials: string;
  colorIndex: number;
  avatarUrl: string | null;
  /** 로그인에 쓰는 기본 주소 */
  primaryAddress: string;
  /** 다중 계정 관리 화면에서 보이는 별칭 주소들 */
  addresses: MailAddress[];
  receiveMode: ReceiveMode;
  /** 집사 등급. 쓸 수 있는 주소 개수와 캣닙 단가를 정한다 (plan.ts) */
  plan: Plan;
  /** 서버가 안 알려줄 수도 있다(옛 API). 그때는 판단하지 않는다 */
  status?: ApprovalStatus;
};

// ---------------------------------------------------------------------------
// API 계약
// ---------------------------------------------------------------------------

export type Paginated<T> = {
  items: T[];
  /** 다음 페이지를 요청할 커서. null이면 끝 */
  nextCursor: string | null;
};

export type SendMessageInput = {
  roomId?: string;
  /** roomId가 없을 때: 새 방을 열 상대 주소들 */
  to?: string[];
  body: string;
  /** 생략하면 방의 마지막 제목을 그대로 이어 쓴다 */
  subject?: string;
  attachmentIds?: string[];
};

/** SSE로 내려오는 실시간 이벤트 */
export type ServerEvent =
  | { type: "message.new"; roomId: string; message: Message }
  | {
      type: "message.delivery";
      messageId: string;
      delivery: DeliveryState;
      error: string | null;
    }
  | { type: "room.updated"; room: Room }
  | { type: "room.read"; roomId: string; unreadCount: number }
  /**
   * 같은 서버 사람이 들어오거나 나갔다. 주소로 알린다 — 받는 쪽은 상대의
   * userId를 모르고 알 이유도 없다.
   */
  | { type: "presence"; address: string; presence: Presence };

// ---------------------------------------------------------------------------
// 상수
// ---------------------------------------------------------------------------

/** 아바타 색 팔레트. 디자인 시안에서 그대로 추출했다. */
export const AVATAR_COLORS = [
  "#4A6FA5",
  "#8A6BA8",
  "#C4703F",
  "#6E8F6A",
  "#B0473A",
  "#7A7A7A",
  "#9AA88F",
  "#A79A87",
  "#8E9AA8",
  "#B0A48F",
  "#9C9488",
] as const;

/** Cloudflare Email Sending 한 통당 최대 크기(본문+첨부). 초과분은 R2로 뺀다. */
export const MAX_SEND_BYTES = 5 * 1024 * 1024;

/** Cloudflare Email Routing 수신 한도 */
export const MAX_RECEIVE_BYTES = 25 * 1024 * 1024;

/** Cloudflare Email Sending 한 통당 최대 수신자 수 (to+cc+bcc 합산) */
export const MAX_RECIPIENTS = 50;

/**
 * 프로필 사진 한 장의 상한.
 *
 * 화면이 올리기 전에 정사각형으로 줄이므로(256px) 실제로는 수십 KB다. 이 값은
 * 줄이기를 건너뛴 요청을 막는 울타리다 — 아바타는 목록에서 수십 개가 한꺼번에
 * 그려지는 그림이라 원본 사진을 그대로 두면 방 목록이 몇 MB가 된다.
 */
export const MAX_AVATAR_BYTES = 512 * 1024;

/** 프로필 사진의 한 변. 레티나에서도 뭉개지지 않을 만큼만 (화면 최대 84px). */
export const AVATAR_SIZE_PX = 256;
